import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import buttonStyles from '../components/Buttons.module.css';
import pawImg from '../assets/paw.png';
import { useAuth } from '../auth/AuthContext';
import * as authApi from '../api/auth';

function useRedirectTo(): string {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo');
  return redirectTo || '/recipes';
}

function useMessage(): string | null {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get('message');
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();
  const redirectTo = useRedirectTo();
  const message = useMessage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [lastResendEmail, setLastResendEmail] = useState('');

  const isValid = useMemo(() => email.trim().length > 3 && password.length >= 6, [email, password]);

  const handleResendConfirmation = async () => {
    // Prevent multiple submissions while loading
    if (resendLoading) return;
    
    // Prevent resubmitting the same email
    const trimmedEmail = email.trim();
    if (trimmedEmail === lastResendEmail) {
      return;
    }
    
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await authApi.resendConfirmation(trimmedEmail);
      setResendSuccess(true);
      setLastResendEmail(trimmedEmail);
      setError('Confirmation email sent! Please check your inbox.');
      setTimeout(() => {
        setResendSuccess(false);
        setShowResend(false);
      }, 5000);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      
      if (status === 404) {
        setError('Email address not found. Please check and try again.');
      } else if (message && message.toLowerCase().includes('already confirmed')) {
        setError('This email is already confirmed. You can log in now.');
        setShowResend(false);
      } else {
        setError('Failed to resend confirmation email. Please try again.');
      }
      setLastResendEmail(trimmedEmail);
    } finally {
      setResendLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowResend(false);
    try {
      await login({ email: email.trim(), password });
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const errorCode = err?.response?.data?.error_code || err?.response?.data?.code;
      const status = err?.response?.status;
      
      // Handle specific error cases with friendly messages
      if (errorCode === 'EMAIL_NOT_CONFIRMED') {
        setError('Please confirm your email before logging in.');
        setShowResend(true);
      } else if (status === 401 || errorCode === 'INVALID_CREDENTIALS') {
        setError('Invalid email or password. Please try again.');
      } else if (status === 429) {
        setError('Too many login attempts. Please try again later.');
      } else if (status >= 500) {
        setError('Our servers are experiencing issues. Please try again in a few moments.');
      } else if (!navigator.onLine) {
        setError('No internet connection. Please check your network and try again.');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError('Unable to log in. Please check your credentials and try again.');
      }
    }
  };

  return (
    <div className="page-container cream-bg">
      <div className="hero-paw-bg">
        <img src={pawImg} className="hero-paw-float paw1" alt="" />
        <img src={pawImg} className="hero-paw-float paw2" alt="" />
        <img src={pawImg} className="hero-paw-float paw3" alt="" />
        <img src={pawImg} className="hero-paw-float paw4" alt="" />
        <img src={pawImg} className="hero-paw-float paw5" alt="" />
        <img src={pawImg} className="hero-paw-float paw6" alt="" />
        <img src={pawImg} className="hero-paw-float paw7" alt="" />
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '2rem' }}>
        <div className="page-intro" style={{ padding: 0 }}>
          <h1 className="page-title">Log in</h1>
          <div className="page-intro-underline" />
          {message && (
            <p style={{ marginTop: '1rem', color: '#16a34a', fontWeight: 500, fontSize: '1rem', textAlign: 'center' }}>
              {message}
            </p>
          )}
        </div>

        <form onSubmit={onSubmit} style={{ background: '#fff', borderRadius: 16, padding: '1.25rem', border: '1px solid rgba(178, 70, 70, 0.18)' }}>
          {error && (
            <div style={{ marginBottom: 12, color: resendSuccess ? '#16a34a' : '#b24646', fontWeight: 600 }}>
              {error}
            </div>
          )}
          
          {showResend && !resendSuccess && (
            <div style={{ 
              marginBottom: 12, 
              padding: '12px', 
              background: '#fff7ed', 
              borderRadius: 8, 
              border: '1px solid #fed7aa' 
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#7c2d12' }}>
                Didn't receive the confirmation email?
              </p>
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resendLoading}
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                style={{ fontSize: '0.9rem', padding: '6px 12px' }}
              >
                {resendLoading ? 'Sending...' : 'Resend confirmation email'}
              </button>
            </div>
          )}

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Email</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            autoComplete="email"
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', marginBottom: 14 }}
          />

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Password</label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            autoComplete="current-password"
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', marginBottom: 16 }}
          />

          <button
            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
            type="submit"
            disabled={!isValid || isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? 'Signing in…' : 'Log in'}
          </button>

          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#555' }}>No account?</span>
            <Link
              to={`/signup?redirectTo=${encodeURIComponent(redirectTo)}`}
              className={buttonStyles.textLinkBtn}
            >
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
